import { useEffect, useState } from 'react'
import { Button, Dialog, DialogBody, DialogContent, DialogFooter, Field, Input, Switch, useToast } from '@bb/ui'
import { ApiError } from '../../api/client'
import { de } from '../../i18n'
import {
  buildSettingChanges, canSaveSetting, clearSecretChanges, forFrontendBlockedBySecret,
  initialFormValues, settingConfirmationLines, type SettingFormValues,
} from './settingsForm'
import { useWriteAdminSetting } from './useSettings'
import type { Setting } from './types'

export interface EditSettingDialogProps {
  setting: Setting | null
  onOpenChange: (open: boolean) => void
}

type View = 'edit' | 'confirm-save' | 'confirm-clear'

const EMPTY_FORM: SettingFormValues = { value: '', description: '', forFrontend: false }

/**
 * §8: editing a single setting. One dialog with three internal views rather
 * than stacked Radix dialogs — `edit` (the form), `confirm-save` (§7's
 * confirmation, naming the setting and every changed field, never a
 * secret's new value) and `confirm-clear` (§8's separate, explicit action
 * for removing an already-stored secret — never reachable by just saving an
 * empty field, see settingsForm.ts's `clearSecretChanges`).
 *
 * The server checks everything this form also checks (the
 * `for_frontend`+`is_secret` conflict, the secret-downgrade refusal) against
 * the merged result regardless — same principle as §7's account deletion:
 * a confirmation in the interface is not access control. Any such refusal
 * that reaches here anyway (a race with another admin, say) surfaces as
 * `ApiError.message` — the server's own German sentence — rather than a
 * generic error.
 */
export function EditSettingDialog({ setting, onOpenChange }: EditSettingDialogProps) {
  const write = useWriteAdminSetting()
  const toast = useToast()
  const [view, setView] = useState<View>('edit')
  const [form, setForm] = useState<SettingFormValues>(EMPTY_FORM)
  const [error, setError] = useState<string | null>(null)

  // A fresh form every time a different setting (or none) is targeted — see
  // DeleteAccountDialog's identical reasoning for `typed`.
  useEffect(() => {
    setForm(setting ? initialFormValues(setting) : EMPTY_FORM)
    setView('edit')
    setError(null)
  }, [setting])

  function updateForm(patch: Partial<SettingFormValues>) {
    setForm((current) => ({ ...current, ...patch }))
  }

  function close() {
    if (write.isPending) return
    onOpenChange(false)
  }

  async function confirmSave() {
    if (!setting) return
    try {
      await write.mutateAsync({ name: setting.name, body: buildSettingChanges(setting, form) })
      toast({ tone: 'success', title: de.settings.toast.saved(setting.name) })
      onOpenChange(false)
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : de.common.unknownError)
    }
  }

  async function confirmClear() {
    if (!setting) return
    try {
      await write.mutateAsync({ name: setting.name, body: clearSecretChanges() })
      toast({ tone: 'success', title: de.settings.toast.cleared(setting.name) })
      onOpenChange(false)
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : de.common.unknownError)
    }
  }

  const blocked = setting !== null && forFrontendBlockedBySecret(setting)
  const canSave = setting !== null && canSaveSetting(setting, form)
  const confirmLines = setting ? settingConfirmationLines(setting, buildSettingChanges(setting, form)) : []

  return (
    <Dialog open={setting !== null} onOpenChange={(open) => !open && close()}>
      <DialogContent width="sm" title={setting?.name ?? ''} description={setting?.description || undefined}>
        {setting === null ? null : view === 'edit' ? (
          <>
            <DialogBody className="space-y-4">
              {setting.isSecret ? (
                <Field label={de.settings.form.newSecretValue} hint={de.settings.form.secretHint}>
                  {({ id }) => (
                    <Input
                      id={id}
                      type="password"
                      autoComplete="off"
                      placeholder={de.settings.form.newSecretPlaceholder}
                      value={form.value}
                      onChange={(event) => updateForm({ value: event.target.value })}
                    />
                  )}
                </Field>
              ) : (
                <Field label={de.settings.form.value}>
                  {({ id }) => (
                    <Input id={id} value={form.value} onChange={(event) => updateForm({ value: event.target.value })} />
                  )}
                </Field>
              )}

              <Field label={de.settings.form.description}>
                {({ id }) => (
                  <Input
                    id={id}
                    value={form.description}
                    onChange={(event) => updateForm({ description: event.target.value })}
                  />
                )}
              </Field>

              <div className="flex items-start gap-3">
                <Switch
                  id="setting-for-frontend"
                  checked={form.forFrontend}
                  disabled={blocked}
                  onCheckedChange={(checked) => updateForm({ forFrontend: checked })}
                  className="mt-0.5"
                />
                <div className="space-y-0.5">
                  <label htmlFor="setting-for-frontend" className="text-base text-ink">
                    {de.settings.form.forFrontend}
                  </label>
                  {blocked ? <p className="text-xs text-warning">{de.settings.form.forFrontendBlocked}</p> : null}
                </div>
              </div>

              {setting.isSecret && setting.hasValue ? (
                <div className="rounded-[var(--radius-control)] border border-rule-strong px-3 py-2.5">
                  <p className="text-xs text-ink-muted">{de.settings.form.clearSecretHint}</p>
                  <Button variant="danger" size="sm" className="mt-2" onClick={() => setView('confirm-clear')}>
                    {de.settings.form.clearSecretButton}
                  </Button>
                </div>
              ) : null}

              {error ? (
                <p role="alert" className="rounded-[var(--radius-control)] bg-danger-soft px-3 py-2 text-base text-danger">
                  {error}
                </p>
              ) : null}
            </DialogBody>
            <DialogFooter>
              <Button variant="secondary" onClick={close}>{de.common.cancel}</Button>
              <Button disabled={!canSave} onClick={() => setView('confirm-save')}>{de.settings.form.save}</Button>
            </DialogFooter>
          </>
        ) : view === 'confirm-save' ? (
          <>
            <DialogBody className="space-y-3">
              <p className="text-base text-ink">{de.settings.confirm.heading(setting.name)}</p>
              <ul className="list-disc space-y-1 pl-5 text-base text-ink">
                {confirmLines.map((line) => <li key={line}>{line}</li>)}
              </ul>
              {error ? (
                <p role="alert" className="rounded-[var(--radius-control)] bg-danger-soft px-3 py-2 text-base text-danger">
                  {error}
                </p>
              ) : null}
            </DialogBody>
            <DialogFooter>
              <Button variant="secondary" disabled={write.isPending} onClick={() => setView('edit')}>
                {de.common.cancel}
              </Button>
              <Button loading={write.isPending} onClick={confirmSave}>{de.settings.confirm.confirmLabel}</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogBody className="space-y-2">
              <p className="text-base text-ink">{de.settings.confirm.clearSecretHeading(setting.name)}</p>
              <p className="text-xs text-ink-muted">{de.settings.confirm.clearSecretDetail}</p>
              {error ? (
                <p role="alert" className="rounded-[var(--radius-control)] bg-danger-soft px-3 py-2 text-base text-danger">
                  {error}
                </p>
              ) : null}
            </DialogBody>
            <DialogFooter>
              <Button variant="secondary" disabled={write.isPending} onClick={() => setView('edit')}>
                {de.common.cancel}
              </Button>
              <Button variant="danger" loading={write.isPending} onClick={confirmClear}>
                {de.settings.confirm.confirmClearLabel}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
